/**
 * Scene-editor category/hierarchy layout.
 *
 * Stored only after the user edits this version:
 *   gameConfig.metadata.categoryData = { folders, categories, order, ... }
 *
 * Until then the editor displays the default version's categoryData, or the
 * base `predefinedGameComponents[].section` grouping. Dashboard-only; the
 * playable engine ignores this.
 */

import _ from "lodash";
import { normalizeCategoryName } from "./playEngineComponentCategoryUtils";
import {
  orderKeyForCategory,
  orderKeyForParent,
  rootLayoutEntryForComponent,
  rootLayoutEntryForFolder,
} from "./playEngineComponentOrderUtils";

export const SCENE_EDITOR_CATEGORY_DATA_KEY = "categoryData";

export const SCENE_EDITOR_CATEGORY_LAYOUT_KEYS = [
  "componentCategoryFolders",
  "componentCategories",
  "rootLayoutOrder",
  "componentOrder",
];

/**
 * Layout-only slice (categories + hierarchy order).
 * @param {object | null | undefined} source
 */
export function pickSceneEditorLayoutMetadata(source) {
  const meta = source || {};
  return {
    componentCategoryFolders: Array.isArray(meta.componentCategoryFolders)
      ? meta.componentCategoryFolders
      : [],
    componentCategories:
      meta.componentCategories && typeof meta.componentCategories === "object"
        ? meta.componentCategories
        : {},
    rootLayoutOrder: Array.isArray(meta.rootLayoutOrder) ? meta.rootLayoutOrder : [],
    componentOrder:
      meta.componentOrder && typeof meta.componentOrder === "object" ? meta.componentOrder : {},
  };
}

/**
 * True when this version owns a category layout (nested or legacy top-level).
 * @param {object | null | undefined} metadata
 */
export function hasOwnSceneEditorCategoryData(metadata) {
  if (!metadata || typeof metadata !== "object") return false;
  if (
    Object.prototype.hasOwnProperty.call(metadata, SCENE_EDITOR_CATEGORY_DATA_KEY) &&
    metadata[SCENE_EDITOR_CATEGORY_DATA_KEY] != null
  ) {
    return true;
  }
  return SCENE_EDITOR_CATEGORY_LAYOUT_KEYS.some((key) => metadata[key] != null);
}

/**
 * Read layout from `metadata.categoryData`, or legacy keys on `metadata` itself.
 * @param {object | null | undefined} metadata
 */
export function extractSceneEditorCategoryData(metadata) {
  if (!metadata || typeof metadata !== "object") {
    return pickSceneEditorLayoutMetadata({});
  }
  const nested = metadata[SCENE_EDITOR_CATEGORY_DATA_KEY];
  if (nested && typeof nested === "object") {
    return pickSceneEditorLayoutMetadata(nested);
  }
  return pickSceneEditorLayoutMetadata(metadata);
}

/**
 * Remove category layout from metadata (nested + legacy top-level keys).
 * @param {object | null | undefined} metadata
 */
export function stripSceneEditorCategoryLayout(metadata) {
  const next = { ...(metadata || {}) };
  delete next[SCENE_EDITOR_CATEGORY_DATA_KEY];
  SCENE_EDITOR_CATEGORY_LAYOUT_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

/**
 * Write layout under `metadata.categoryData` and drop legacy top-level copies.
 * @param {object | null | undefined} metadata
 * @param {object} categoryData
 */
export function writeSceneEditorCategoryData(metadata, categoryData) {
  const next = stripSceneEditorCategoryLayout(metadata);
  next[SCENE_EDITOR_CATEGORY_DATA_KEY] = pickSceneEditorLayoutMetadata(categoryData);
  return next;
}

/**
 * Full version document for the company's default version, if present in `versions`.
 * @param {Array<object> | null | undefined} versions
 * @param {object | string | null | undefined} defaultVersion
 */
export function findDefaultVersionRecord(versions, defaultVersion) {
  const id = defaultVersion?.versionID || defaultVersion?._id || defaultVersion;
  if (!id) return null;
  return (versions || []).find((v) => String(v._id) === String(id)) || null;
}

/**
 * Layout to display: this version's categoryData if present, else default
 * version's, else base sections. Does not write to the version.
 * @param {{
 *   versionMetadata?: object,
 *   versions?: Array,
 *   defaultVersion?: object,
 *   basePredefinedEntries?: Array,
 *   selectedVersionId?: string,
 * }} p
 */
export function resolveEffectiveCategoryData({
  versionMetadata,
  versions,
  defaultVersion,
  basePredefinedEntries,
  selectedVersionId,
} = {}) {
  if (hasOwnSceneEditorCategoryData(versionMetadata)) {
    return extractSceneEditorCategoryData(versionMetadata);
  }
  const defaultRecord = findDefaultVersionRecord(versions, defaultVersion);
  if (defaultRecord && String(defaultRecord._id) !== String(selectedVersionId || "")) {
    const inheritedMeta = _.get(defaultRecord, "gameConfig.metadata");
    if (hasOwnSceneEditorCategoryData(inheritedMeta)) {
      return extractSceneEditorCategoryData(inheritedMeta);
    }
  }
  return buildSceneEditorMetadataFromSections(basePredefinedEntries);
}

/**
 * @param {Array<{ id: string, key: string, section?: string, parentKey?: string }> | null | undefined} basePredefinedEntries
 * @returns {{
 *   componentCategoryFolders: string[],
 *   componentCategories: Record<string, string>,
 *   rootLayoutOrder: string[],
 *   componentOrder: Record<string, string[]>,
 * }}
 */
export function buildSceneEditorMetadataFromSections(basePredefinedEntries) {
  const entries = basePredefinedEntries || [];
  const byKey = _.keyBy(entries, "key");

  const roots = [];
  /** @type {Record<string, string[]>} */
  const childrenByParentId = {};

  entries.forEach((entry) => {
    if (!entry?.id) return;
    const parentKey = entry.parentKey || null;
    const parent = parentKey ? byKey[parentKey] : null;
    if (parent && parent !== entry && parent.id) {
      if (!childrenByParentId[parent.id]) childrenByParentId[parent.id] = [];
      childrenByParentId[parent.id].push(entry.id);
      return;
    }
    roots.push(entry);
  });

  /** @type {string[]} */
  const folders = [];
  const folderSeen = new Set();
  /** @type {Record<string, string>} */
  const componentCategories = {};
  /** @type {Record<string, string[]>} */
  const idsBySection = {};
  /** @type {string[]} */
  const uncategorizedRootIds = [];

  roots.forEach((entry) => {
    const section = normalizeCategoryName(entry.section);
    if (section) {
      if (!folderSeen.has(section)) {
        folderSeen.add(section);
        folders.push(section);
      }
      componentCategories[entry.id] = section;
      if (!idsBySection[section]) idsBySection[section] = [];
      idsBySection[section].push(entry.id);
    } else {
      uncategorizedRootIds.push(entry.id);
    }
  });

  /** @type {Record<string, string[]>} */
  const componentOrder = {
    root: [...uncategorizedRootIds],
  };
  folders.forEach((folder) => {
    componentOrder[orderKeyForCategory(folder)] = idsBySection[folder] || [];
  });
  Object.keys(childrenByParentId).forEach((parentId) => {
    componentOrder[orderKeyForParent(parentId)] = childrenByParentId[parentId];
  });

  const rootLayoutOrder = [
    ...folders.map((name) => rootLayoutEntryForFolder(name)),
    ...uncategorizedRootIds.map((id) => rootLayoutEntryForComponent(id)),
  ];

  return {
    componentCategoryFolders: folders,
    componentCategories,
    rootLayoutOrder,
    componentOrder,
  };
}
